import { BaseService } from './base-service';
import {
	GetDataExtractionRequestDTO,
	GetDataExtractionResponseDTO,
	CreateDataExtractionRequestDTO,
	CreateDataExtractionResponseDTO,
	UpdateDataExtractionRequestDTO,
	UpdateDataExtractionResponseDTO,
	CreateDataExtractionModelRequestDTO,
	CreateDataExtractionModelResponseDTO
} from '@/core/dto/data-extraction';
import { GetErgonomicToolListMapper } from '@/core/mapper/ergonomic-tool/get-ergonomic-tool-list';
import {
	GetDataExtractionListMapper,
	GetDataExtractionModelMapper,
	GetDataExtractionMapper,
	GetDataExtractionModelListMapper
} from '@/core/mapper/data-extraction';
import type {
	GetDataExtractionListRequestDTO,
	GetDataExtractionListResponseDTO,
	DeleteDataExtractionRequestDTO,
	DeleteDataExtractionResponseDTO,
	DeleteDataExtractionsRequestDTO,
	DeleteDataExtractionsResponseDTO,
	GetActionPlanColumnsRequestDTO,
	GetActionPlanColumnsResponseDTO,
	GetAllDataExtractionIdsResponseDTO,
	GetAllDataExtractionIdsRequestDTO,
	CreateDataExtractionQueueRequestDTO,
	CreateDataExtractionQueueResponseDTO,
	GetDataExtractionCsvToDownloadRequestDTO,
	GetDataExtractionCsvToDownloadResponseDTO,
	GetDataExtractionModelListRequestDTO,
	GetDataExtractionModelListResponseDTO,
	GetDataExtractionModelRequestDTO,
	GetDataExtractionModelResponseDTO,
	DeleteDataExtractionModelRequestDTO,
	DeleteDataExtractionModelResponseDTO,
	CreateDataExtractionReportRequestDTO,
	CreateDataExtractionReportResponseDTO,
	DeleteDataExtractionReportRequestDTO,
	DeleteDataExtractionReportResponseDTO,
	GetDataExtractionSelectedReportsCountRequestDTO,
	GetDataExtractionSelectedReportsCountResponseDTO,
	GetExtractionFilesListRequestDTO,
	GetExtractionFilesListResponseDTO,
	CreateDataExtractionByModelRequestDTO,
	CreateDataExtractionByModelResponseDTO,
	GetDataExtractionRelatedFilesResponseDTO,
	GetDataExtractionRelatedFilesRequestDTO,
	CreateDataExtractionFileRequestDTO,
	CreateDataExtractionFileResponseDTO,
	DeleteDataExtractionFileRequestDTO,
	DeleteDataExtractionFileResponseDTO
} from '@/core/dto/data-extraction';
import { GetExtractionFilesListMapper } from '@/core/mapper/data-extraction';
import type { DataExtractionFile } from '@/types/entities/DataExtraction';

export class DataExtractionService extends BaseService<CreateDataExtractionResponseDTO> {
	private static instance: DataExtractionService;

	private constructor(public readonly basePath: string = '/data-extraction') {
		super();
	}

	public static getInstance(): DataExtractionService {
		if (!DataExtractionService.instance) {
			DataExtractionService.instance = new DataExtractionService();
		}
		return DataExtractionService.instance;
	}

	public async create(params: CreateDataExtractionRequestDTO): Promise<CreateDataExtractionResponseDTO> {
		const url = this.basePath + '/';
		const { data } = await this.getInstance().post<CreateDataExtractionResponseDTO>(url, params);
		return data;
	}

	public async update(params: UpdateDataExtractionRequestDTO): Promise<UpdateDataExtractionResponseDTO> {
		const url = `${this.basePath}/${params.id}`;
		const { data } = await this.getInstance().put<UpdateDataExtractionResponseDTO>(url, params);
		return data;
	}

	public async findOne(params: GetDataExtractionRequestDTO) {
		const url = this.basePath + `/${params.data_extraction_id}`;
		const { data } = await this.getInstance().get<GetDataExtractionResponseDTO>(url, { params });
		const mapper = new GetDataExtractionMapper(data);
		return mapper.toDomain();
	}

	public async destroy(params: DeleteDataExtractionRequestDTO): Promise<DeleteDataExtractionResponseDTO> {
		const url = `${this.basePath}/${params.data_extraction_id}`;
		const { data } = await this.getInstance().delete<DeleteDataExtractionResponseDTO>(url, { params });
		return data;
	}

	public async deleteDataExtractions(
		params: DeleteDataExtractionsRequestDTO
	): Promise<DeleteDataExtractionsResponseDTO> {
		const url = `${this.basePath}/bulk-delete`;
		const { data } = await this.getInstance().delete<DeleteDataExtractionsResponseDTO>(url, {
			data: params
		});
		return data;
	}

	public async getAllExtractionIds(
		params: GetAllDataExtractionIdsRequestDTO
	): Promise<GetAllDataExtractionIdsResponseDTO> {
		const url = `${this.basePath}/ids`;
		const { data } = await this.getInstance().get<GetAllDataExtractionIdsResponseDTO>(url, { params });
		return data;
	}

	public async getActionPlanColumns(
		params: GetActionPlanColumnsRequestDTO
	): Promise<GetActionPlanColumnsResponseDTO> {
		const url = `${this.basePath}/${params.data_extraction_id}/action-plan-columns`;
		const { data } = await this.getInstance().get<GetActionPlanColumnsResponseDTO>(url);
		return data;
	}

	public async createModel(
		params: CreateDataExtractionModelRequestDTO
	): Promise<CreateDataExtractionModelResponseDTO> {
		const url = `${this.basePath}/models`;
		const { data } = await this.getInstance().post<CreateDataExtractionModelResponseDTO>(url, params);
		return data;
	}

	public async createDataExtractionQueue(
		params: CreateDataExtractionQueueRequestDTO
	): Promise<CreateDataExtractionQueueResponseDTO> {
		const url = `${this.basePath}/${params.data_extraction_id}/send-to-queue`;
		const { data } = await this.getInstance().post<CreateDataExtractionQueueResponseDTO>(url, params);
		return data;
	}

	public async getDataExtractionCSVToDownload(
		params: GetDataExtractionCsvToDownloadRequestDTO
	): Promise<GetDataExtractionCsvToDownloadResponseDTO> {
		const url = `${this.basePath}/${params.data_extraction_id}/download-file`;
		const { data } = await this.getInstance().get<GetDataExtractionCsvToDownloadResponseDTO>(url, { params });
		return data;
	}

	public async findAll(parameters: GetDataExtractionListRequestDTO) {
		const url = this.basePath + '/';
		const params = GetErgonomicToolListMapper.toDTO(parameters);
		const { data } = await this.getInstance().get<GetDataExtractionListResponseDTO>(url, { params });
		return new GetDataExtractionListMapper(data).toDomain();
	}

	public async findAllModels(
		params: GetDataExtractionModelListRequestDTO
	): Promise<GetDataExtractionModelListResponseDTO> {
		const url = `${this.basePath}/models`;
		const { data } = await this.getInstance().get<GetDataExtractionModelListResponseDTO>(url, { params });
		return new GetDataExtractionModelListMapper(data).toDomain();
	}

	public async findOneModel(params: GetDataExtractionModelRequestDTO): Promise<GetDataExtractionModelResponseDTO> {
		const url = `${this.basePath}/models/${params.data_extraction_model_id}`;
		const { data } = await this.getInstance().get<GetDataExtractionModelResponseDTO>(url, { params });
		return new GetDataExtractionModelMapper(data).toDomain();
	}

	public async destroyModel(
		params: DeleteDataExtractionModelRequestDTO
	): Promise<DeleteDataExtractionModelResponseDTO> {
		const url = `${this.basePath}/models/${params.data_extraction_model_id}`;
		const { data } = await this.getInstance().delete<DeleteDataExtractionModelResponseDTO>(url, { params });
		return data;
	}

	public async createDataExtractionReport(
		params: CreateDataExtractionReportRequestDTO
	): Promise<CreateDataExtractionReportResponseDTO> {
		const url = `${this.basePath}/${params.data_extraction_id}/add-report`;
		const { data } = await this.getInstance().post<CreateDataExtractionReportResponseDTO>(url, params);
		return data;
	}

	public async deleteDataExtractionReport(
		params: DeleteDataExtractionReportRequestDTO
	): Promise<DeleteDataExtractionReportResponseDTO> {
		const url = `${this.basePath}/${params.data_extraction_id}/remove-report`;
		const { data } = await this.getInstance().delete<DeleteDataExtractionReportResponseDTO>(url, {
			data: params
		});
		return data;
	}

	public async getDataExtractionReportsSelectedCount(
		params: GetDataExtractionSelectedReportsCountRequestDTO
	): Promise<GetDataExtractionSelectedReportsCountResponseDTO> {
		const url = `${this.basePath}/${params.data_extraction_id}/reports/count`;
		const { data } = await this.getInstance().get<GetDataExtractionSelectedReportsCountResponseDTO>(url, {
			params
		});
		return data;
	}

	public async getFilesList(params: GetExtractionFilesListRequestDTO): Promise<DataExtractionFile> {
		const url = `${this.basePath}/files/list`;
		const { data } = await this.getInstance().get<GetExtractionFilesListResponseDTO>(url, { params });
		return new GetExtractionFilesListMapper(data).toDomain();
	}

	public async createByModel(
		params: CreateDataExtractionByModelRequestDTO
	): Promise<CreateDataExtractionByModelResponseDTO> {
		const url = `${this.basePath}/models/${params.data_extraction_model_id}/create-extraction`;
		const { data } = await this.getInstance().post<CreateDataExtractionByModelResponseDTO>(url, params);
		return data;
	}

	public async getRelatedFiles(
		params: GetDataExtractionRelatedFilesRequestDTO
	): Promise<GetDataExtractionRelatedFilesResponseDTO> {
		const url = `${this.basePath}/${params.id}/files`;
		const { data } = await this.getInstance().get<GetDataExtractionRelatedFilesResponseDTO>(url, { params });
		return data;
	}

	public async addFile(params: CreateDataExtractionFileRequestDTO): Promise<CreateDataExtractionFileResponseDTO> {
		const url = `${this.basePath}/${params.id}/add-file`;
		const { data } = await this.getInstance().post<CreateDataExtractionFileResponseDTO>(url, params);
		return data;
	}

	public async deleteFile(params: DeleteDataExtractionFileRequestDTO): Promise<DeleteDataExtractionFileResponseDTO> {
		const url = `${this.basePath}/${params.id}/remove-file`;
		const { data } = await this.getInstance().delete<DeleteDataExtractionFileResponseDTO>(url, { params });
		return data;
	}
}
